import { useMemoizedFn } from 'ahooks';
import type {
    IProcessBatchParams,
    IProcessChunkPagesParams,
    IProcessChunkParams,
    IProcessChunksWithConcurrencyParams,
    IStaticInfo,
} from '../types';
import { chunkArray } from '../utils/common';
import { findMatchesInPage } from '../utils/pdf-text-layer';

export interface UseSearchMatcherProps {
    totalPages: number;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js text content
    getCachedTextContent: (pageNum: number) => Promise<any | null>;
    /** 检查本次搜索是否已被取消 */
    isSearchCancelled: (searchId: number) => boolean;
    /** 找到当前页匹配时立即高亮 */
    onPageMatched?: (pageNum: number) => Promise<void>;
}

/**
 * 搜索匹配 Hook
 *
 * 从 usePdfSearch 拆分：封装跨页面文本匹配的批量/并发处理逻辑。
 * 不持有 UI 状态，通过回调通知调用方。
 */
export function useSearchMatcher(props: UseSearchMatcherProps) {
    const { getCachedTextContent, isSearchCancelled, onPageMatched } = props;

    const processPageMatches = useMemoizedFn(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js text content
        (pageNum: number, textContent: any, term: string, value: string): IStaticInfo[] => {
            if (!textContent) return [];
            return findMatchesInPage(pageNum, textContent, term, value);
        }
    );

    const processChunkPages = useMemoizedFn(
        async (
            params: IProcessChunkPagesParams
        ): Promise<{ matches: IStaticInfo[]; highlightPageNum?: number; cancelled: boolean }> => {
            const { startPage, endPage, searchId, term, value, currentPage } = params;
            if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };

            const pageNums = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
            const textContents = await Promise.all(pageNums.map((pageNum) => getCachedTextContent(pageNum)));
            if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };

            const pageResults = pageNums.map((pageNum, idx) => ({
                pageNum,
                matches: processPageMatches(pageNum, textContents[idx], term, value),
            }));

            const allMatches = pageResults.flatMap((result) => result.matches);
            const highlightPageNum = pageResults.find(
                (result) => result.matches.length > 0 && result.pageNum === currentPage
            )?.pageNum;

            return { matches: allMatches, highlightPageNum, cancelled: false };
        }
    );

    const processChunk = useMemoizedFn(
        async (
            params: IProcessChunkParams
        ): Promise<{ matches: IStaticInfo[]; highlightPageNum?: number; cancelled: boolean }> => {
            const { searchId } = params;
            await new Promise((resolve) => setTimeout(resolve, 0));
            if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };

            const chunkResult = await processChunkPages(params);
            if (chunkResult.cancelled) return chunkResult;

            if (chunkResult.highlightPageNum && onPageMatched) {
                await onPageMatched(chunkResult.highlightPageNum);
            }
            return chunkResult;
        }
    );

    const processBatch = useMemoizedFn(async (params: IProcessBatchParams): Promise<IStaticInfo[]> => {
        const { batch, searchId, term, value, currentPage } = params;
        if (isSearchCancelled(searchId)) return [];

        const batchResults = await Promise.all(
            batch.map((chunk) =>
                processChunk({
                    startPage: chunk.startPage,
                    endPage: chunk.endPage,
                    searchId,
                    term,
                    value,
                    currentPage,
                })
            )
        );
        return batchResults.flatMap((result) => (result.cancelled ? [] : result.matches));
    });

    const processChunksWithConcurrency = useMemoizedFn(
        async (
            params: IProcessChunksWithConcurrencyParams
        ): Promise<{ matches: IStaticInfo[]; cancelled: boolean }> => {
            const { chunks, searchId, term, value, currentPage, concurrency = 2 } = params;
            const batches = chunkArray(chunks, concurrency);
            if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };

            const allMatches: IStaticInfo[] = [];
            for (const batch of batches) {
                if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };
                try {
                    const batchMatches = await processBatch({ batch, searchId, term, value, currentPage });
                    allMatches.push(...batchMatches);
                } catch (error) {
                    console.warn('batch process failed:', error);
                }
            }

            if (isSearchCancelled(searchId)) return { matches: [], cancelled: true };
            return { matches: allMatches, cancelled: false };
        }
    );

    return { processChunksWithConcurrency };
}
